import type {ReactElement, ReactNode} from 'react';
import type {FailedMessageRowProps} from './failed-message-row.types';
import {FailedMessageType} from '@Components/ai-chat-window/ai-chat-window.types';
import {Text, TextSize, TextType} from '@Components/text';
import styles from './failed-message-row.module.scss';

export function FailedMessageRow({...props}: FailedMessageRowProps): ReactElement {
  const getClassNames = (): string => {
    switch (props.message.failureType) {
      case FailedMessageType.TOO_MANY_REQUESTS:
      case FailedMessageType.INTERNAL_ERROR:
      case FailedMessageType.USER_IMAGE_PROCESSING_ERROR:
      case FailedMessageType.CREDITS_EXHAUSTED:
      case FailedMessageType.DAILY_LIMIT_REACHED:
      case FailedMessageType.CONTENT_POLICY_VIOLATION:
      case FailedMessageType.UNSUPPORTED_LANGUAGE:
        return 'content-danger';
      case FailedMessageType.STOPPED:
        return '';
      default:
        throw new Error(`Unhandled failed message failureType: ${props.message.failureType} in getClassNames`);
    }
  };

  const getFailedClassNames = (): string => {
    return `flex-center ${styles.failedMessage} ${getClassNames()}`;
  };

  const getFailedMessageIconSvg = (): ReactNode => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
        <path
          d="M13.6727 12.6759H2.2613L7.96679 2.79329L13.6727 12.6759ZM8.71429 2.36215C8.38237 1.78684 7.55166 1.78684 7.21973 2.36215L1.51424 12.2447C1.18232 12.8201 1.59745 13.5391 2.26175 13.5391H13.6732C14.3375 13.5391 14.7526 12.8201 14.4207 12.2447L8.71518 2.36215H8.71429Z"
          fill="#9C002F"
        />
        <path
          d="M7.96724 5.81396C8.20573 5.81396 8.39883 6.00707 8.39883 6.24556V8.5468C8.39883 8.78529 8.20573 8.97839 7.96724 8.97839C7.72875 8.97839 7.53564 8.78529 7.53564 8.5468V6.24556C7.53564 6.00707 7.72875 5.81396 7.96724 5.81396Z"
          fill="#9C002F"
        />
        <path
          d="M7.53564 10.8476C7.53564 10.6091 7.72875 10.416 7.96724 10.416H7.97302C8.21107 10.416 8.40462 10.6091 8.40462 10.8476C8.40462 11.0861 8.21151 11.2792 7.97302 11.2792H7.96724C7.72875 11.2792 7.53564 11.0861 7.53564 10.8476Z"
          fill="#9C002F"
        />
      </svg>
    );
  };

  const getStoppedMessageIconSvg = (): ReactNode => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M8 1.03906C11.8444 1.03919 14.9613 4.15564 14.9614 8C14.9614 11.8444 11.8444 14.9613 8 14.9614C4.1556 14.9613 1.03864 11.8444 1.03857 8C1.03868 4.15563 4.15563 1.03917 8 1.03906ZM8 2.03906C4.70791 2.03917 2.03917 4.70791 2.03906 8C2.03913 11.2921 4.70789 13.9613 8 13.9614C11.2921 13.9613 13.9614 11.2921 13.9614 8C13.9613 4.70793 11.2921 2.03919 8 2.03906ZM9.3335 5.5C9.9777 5.50009 10.4999 6.0223 10.5 6.6665V9.3335C10.4999 9.9777 9.9777 10.4999 9.3335 10.5H6.6665C6.0223 10.4999 5.50009 9.9777 5.5 9.3335V6.6665C5.50009 6.0223 6.0223 5.50009 6.6665 5.5H9.3335ZM6.6665 6.5C6.57459 6.50009 6.50009 6.57459 6.5 6.6665V9.3335C6.50009 9.42541 6.57459 9.49991 6.6665 9.5H9.3335C9.42541 9.49991 9.49991 9.42541 9.5 9.3335V6.6665C9.49991 6.57459 9.42541 6.50009 9.3335 6.5H6.6665Z"
          fill="#757589"
        />
      </svg>
    );
  };

  const getFailedMessageIcon = (): ReactNode => {
    switch (props.message.failureType) {
      case FailedMessageType.INTERNAL_ERROR:
      case FailedMessageType.TOO_MANY_REQUESTS:
      case FailedMessageType.USER_IMAGE_PROCESSING_ERROR:
      case FailedMessageType.CREDITS_EXHAUSTED:
      case FailedMessageType.DAILY_LIMIT_REACHED:
      case FailedMessageType.CONTENT_POLICY_VIOLATION:
      case FailedMessageType.UNSUPPORTED_LANGUAGE:
        return getFailedMessageIconSvg();
      case FailedMessageType.STOPPED:
        return getStoppedMessageIconSvg();
      default:
        throw new Error(`Unhandled failed message failureType: ${props.message.failureType} in getFailedMessageIcon`);
    }
  };

  const getFailedMessageText = (): string => {
    switch (props.message.failureType) {
      case FailedMessageType.INTERNAL_ERROR:
        return window.i18next.t('pmwjs_unexpected_error');
      case FailedMessageType.STOPPED:
        return window.i18next.t('pmwjs_request_stopped');
      case FailedMessageType.TOO_MANY_REQUESTS:
        return window.i18next.t('pmwjs_too_many_requests');
      case FailedMessageType.USER_IMAGE_PROCESSING_ERROR:
        return window.i18next.t('pmwjs_user_image_processing_error');
      case FailedMessageType.CREDITS_EXHAUSTED:
        return window.i18next.t('pmwjs_insufficient_credits');
      case FailedMessageType.DAILY_LIMIT_REACHED:
        return window.i18next.t('pmwjs_daily_limit_reached_try_later');
      case FailedMessageType.CONTENT_POLICY_VIOLATION:
        return window.i18next.t('pmwjs_content_policy_copyright_or_sensitive');
      case FailedMessageType.UNSUPPORTED_LANGUAGE:
        return window.i18next.t('pmwjs_unsupported_language');
      default:
        throw new Error(`Unhandled failed message failureType: ${props.message.failureType} in getFailedMessageText`);
    }
  };

  const getFailedMessage = (): ReactElement => {
    return (
      <div className={getFailedClassNames()}>
        {getFailedMessageIcon()}
        <Text val={getFailedMessageText()} className={'spacing-m-l-1'} type={TextType.BODY} size={TextSize.SMALL} />
        {props.showRetry ? (
          <Text
            type={TextType.BODY}
            onClick={() => {
              void props.onRetry();
            }}
            val={window.i18next.t('pmwjs_retry')}
            bold
            underline
            className={`_cursor-pointer spacing-m-l-1`}
            size={TextSize.SMALL}
          />
        ) : null}
      </div>
    );
  };

  return getFailedMessage();
}
